import {
  buildDecisionShareText,
  candidateId,
  compareSeatDuel,
  createSeatDecision,
  seatDuelLabels,
  seatLayoutFor,
  starterVenues,
  type SeatCandidate,
  type SeatDecisionRecord,
  type SeatDuelCriterion,
  type SeatLayoutDefinition,
} from "@anjabom/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BottomSheet } from "../components/BottomSheet";
import { ExternalLink } from "../components/ExternalLink";
import { useLocalState } from "../state/LocalStateProvider";
import { track } from "../lib/analytics";
import "../styles/seat-duel.css";

const choices = starterVenues.flatMap((venue) =>
  venue.spaces.flatMap((space) => {
    const layout = seatLayoutFor(venue.id, space.id);
    return layout?.precision === "seat" &&
      layout.floors.some((floor) =>
        floor.sections.some((section) => section.rows?.length),
      )
      ? [{ venue, space, layout }]
      : [];
  }),
);
const label = (seat: SeatCandidate | null) =>
  seat
    ? `${seat.sectionCode} ${seat.rowLabel}열 ${seat.seatNumber}번`
    : "열·번호 넣기";
const aisleLabel = (value: boolean | null | undefined) =>
  value === true ? "통로 옆" : value === false ? "통로 옆 아님" : "모름";
type Step = "map" | "compare" | "decision" | "saved";

export function SeatDuelPage() {
  useEffect(() => {
    track("first_visit_viewed");
  }, []);
  const { state, saveSeatDecision, storageProblem } = useLocalState();
  const location = useLocation();
  const requestedDecisionId =
    typeof location.state?.seatDecisionId === "string"
      ? location.state.seatDecisionId
      : null;
  const openedDecisionId = useRef<string | null>(null);
  const initialChoice =
    choices.find(
      (choice) =>
        state.candidates.length >= 2 &&
        state.candidates
          .slice(0, 2)
          .every(
            (candidate) =>
              candidate.layoutVersionId === choice.layout.layoutVersionId,
          ),
    ) ??
    choices.find((choice) => choice.venue.id === "blue-square") ??
    choices[0]!;
  const [choiceId, setChoiceId] = useState(
    initialChoice.layout.layoutVersionId,
  );
  const choice =
    choices.find((item) => item.layout.layoutVersionId === choiceId) ??
    initialChoice;
  const { venue, space, layout } = choice;
  const [a, setA] = useState<SeatCandidate | null>(
    state.candidates[0]?.layoutVersionId === layout.layoutVersionId
      ? (state.candidates[0] ?? null)
      : null,
  );
  const [b, setB] = useState<SeatCandidate | null>(
    state.candidates[1]?.layoutVersionId === layout.layoutVersionId
      ? (state.candidates[1] ?? null)
      : null,
  );
  const [step, setStep] = useState<Step>("map");
  const [criterion, setCriterion] = useState<SeatDuelCriterion>("aisle");
  const [chosen, setChosen] = useState<"A" | "B" | null>(null);
  const [note, setNote] = useState("");
  const [eventTitle, setEventTitle] = useState(state.viewingPlan.eventTitle);
  const [saved, setSaved] = useState<SeatDecisionRecord | null>(null);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState("");
  const comparison = useMemo(
    () => compareSeatDuel(layout, a, b, criterion),
    [layout, a, b, criterion],
  );
  const choose = (value: "A" | "B") => {
    setChosen(value);
    setStep("decision");
    setMessage("");
  };
  const revise = () => {
    setStep("compare");
    setChosen(null);
    setMessage("");
  };
  const record = (): SeatDecisionRecord | null => {
    if (!a || !b || !chosen || !comparison.valid) return null;
    const primary = chosen === "A" ? a : b,
      fallback = chosen === "A" ? b : a;
    const result = createSeatDecision(
      [a, b],
      { ...state.viewingPlan, eventTitle },
      primary.candidateId,
      [fallback.candidateId],
      [],
      "unknown_ok",
      note,
    );
    return {
      ...result,
      ...(saved ? { id: saved.id, clientOpId: saved.clientOpId } : {}),
      comparisonCriterion: criterion,
    };
  };
  const text = (value: SeatDecisionRecord | null) =>
    value
      ? `${buildDecisionShareText(value, true)}\n내 비교 기준: ${seatDuelLabels[value.comparisonCriterion ?? criterion]}\n${comparison.reason}\n시설 전사 좌표 참고 · 실제 시야·이번 회차 무대·판매 여부는 미확인.\n시설 자료 확인일 ${layout.source.checkedAt}\n${layout.source.url}`
      : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        text(step === "saved" ? saved : record()),
      );
      setMessage("결정문을 복사했어요. 아래 원문도 확인할 수 있어요.");
    } catch {
      setMessage(
        "복사하지 못했어요. 아래 공유 원문을 직접 선택해 복사해 주세요.",
      );
    }
  };
  const save = () => {
    const value = record();
    if (!value) return;
    // Keep one operation identity if the existing storage owner retains a failed write in memory.
    setSaved(value);
    if (!saveSeatDecision(value)) {
      setMessage(
        state.seatDecisions.length >= 24 &&
          !state.seatDecisions.some((entry) => entry.id === value.id)
          ? "저장한 결정 24개가 있어요. 내 기록에서 불필요한 결정을 직접 지우거나 백업한 뒤 다시 저장해 주세요. 기존 결정은 보존했어요."
          : "기기에 저장하지 못했어요. 입력은 그대로예요. 설정·데이터 관리에서 저장 상태를 확인하고 다시 시도해 주세요.",
      );
      return;
    }
    setSaved(value);
    setStep("saved");
    setMessage("");
  };
  const reopen = useCallback((value: SeatDecisionRecord) => {
    if (!value.comparisonCriterion) {
      setMessage(
        "이전 방식으로 저장한 결정이에요. 아래 후기 근거로 후보 비교 또는 내 기록에서 원래 기준 그대로 열어 주세요.",
      );
      return;
    }
    const next = choices.find(
      (item) =>
        item.layout.layoutVersionId === value.candidates[0]?.layoutVersionId,
    );
    const first = value.candidates[0],
      second = value.candidates[1];
    if (
      !next ||
      !first ||
      !second ||
      !compareSeatDuel(
        next.layout,
        first,
        second,
        value.comparisonCriterion ?? "aisle",
      ).valid
    ) {
      setMessage(
        "이 결정은 기존 후보 비교에서 열어 주세요. 전사 좌표가 없는 배치도 그곳에서 확인할 수 있어요.",
      );
      return;
    }
    setChoiceId(next.layout.layoutVersionId);
    setA(first);
    setB(second);
    setCriterion(value.comparisonCriterion ?? "aisle");
    setChosen(value.chosenCandidateId === first.candidateId ? "A" : "B");
    setNote(value.note);
    setEventTitle(value.eventTitle);
    setSaved(value);
    setStep("saved");
    setMessage("");
  }, []);
  useEffect(() => {
    if (!requestedDecisionId) {
      openedDecisionId.current = null;
      return;
    }
    if (openedDecisionId.current === requestedDecisionId) return;
    const value = state.seatDecisions.find(
      (decision) => decision.id === requestedDecisionId,
    );
    openedDecisionId.current = requestedDecisionId;
    if (value) reopen(value);
    else setMessage("이 기기에서 해당 결정을 찾지 못했어요. 내 기록을 다시 확인해 주세요.");
  }, [requestedDecisionId, state.seatDecisions, reopen]);
  const selected = chosen === "A" ? a : b,
    other = chosen === "A" ? b : a;
  return (
    <div className="seat-duel">
      <div className="aj-body">
        {step !== "map" && (
          <button
            className="aj-back"
            onClick={() => {
              setStep(
                step === "compare"
                  ? "map"
                  : step === "decision"
                    ? "compare"
                    : "decision",
              );
              setMessage("");
            }}
          >
            ←{" "}
            {step === "compare" ? "좌석도 다시 보기" : "기준과 후보 다시 보기"}
          </button>
        )}
        <div className="aj-venue">
          {venue.name} · {space.name}
        </div>
        <h1>
          {step === "map" ? (
            <>
              두 자리 사이,
              <br />
              무엇이 다른가요?
            </>
          ) : step === "compare" ? (
            <>
              내가 중요하게 보는
              <br />한 가지로 비교해요.
            </>
          ) : step === "saved" ? (
            <>
              예매할 때,
              <br />이 순서로 꺼내 봐요.
            </>
          ) : (
            <>
              내 기준과 함께
              <br />
              순서를 남겨요.
            </>
          )}
        </h1>
        {step === "map" && (
          <label className="aj-place">
            시설 배치
            <select
              aria-label="비교할 시설 배치"
              value={choiceId}
              onChange={(event) => {
                setChoiceId(event.target.value);
                setA(null);
                setB(null);
                setSaved(null);
                setChosen(null);
                setMessage("");
              }}
            >
              {choices.map((item) => (
                <option
                  key={item.layout.layoutVersionId}
                  value={item.layout.layoutVersionId}
                >
                  {item.venue.name} · {item.space.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {(step === "map" || step === "compare") && (
          <>
            <DuelMap
              key={layout.layoutVersionId}
              layout={layout}
              a={a}
              b={b}
              compact={step === "compare"}
            />
            <div className="aj-candidate-pair">
              {[
                { id: "A", seat: a },
                { id: "B", seat: b },
              ].map((item) => (
                <button key={item.id} onClick={() => setEditing(true)}>
                  <i className={`aj-${item.id.toLowerCase()}`}>{item.id}</i>
                  <span>
                    <strong>{label(item.seat)}</strong>
                    <small>
                      {item.seat?.expectedPrice == null
                        ? "가격 미입력"
                        : `${item.seat.expectedPrice.toLocaleString("ko-KR")}원 · 내 입력`}
                    </small>
                  </span>
                  <span aria-hidden="true">✎</span>
                </button>
              ))}
            </div>
          </>
        )}
        {step === "map" && (
          <>
            <p className="aj-map-intro">
              예매 중인 두 후보를 같은 좌석도에 놓아요.
              <br />
              시설 좌표로 실제 시야를 대신 판단하지 않아요.
            </p>
            {comparison.valid ? (
              <button className="aj-primary" onClick={() => setStep("compare")}>
                이 두 자리 비교하기 →
              </button>
            ) : (
              <button className="aj-primary" onClick={() => setEditing(true)}>
                두 자리의 열·번호 넣기 →
              </button>
            )}
            <button className="aj-edit" onClick={() => setEditing(true)}>
              열·번호와 예상 가격 바꾸기
            </button>
          </>
        )}
        {step === "compare" && (
          <>
            <div className="aj-priority">
              <h2>내 우선순위는</h2>
              <div role="group" aria-label="비교 기준">
                {(["aisle", "center", "price"] as const).map((id) => (
                  <button
                    aria-pressed={criterion === id}
                    key={id}
                    onClick={() => {
                      setCriterion(id);
                      setChosen(null);
                      setMessage("");
                    }}
                  >
                    {seatDuelLabels[id]}
                  </button>
                ))}
              </div>
            </div>
            <section className="aj-differences" aria-label="A와 B의 조건">
              <div className="aj-layer-label">
                시설 전사에서 읽은 구조 · A / B
              </div>
              <div className={criterion === "aisle" ? "aj-focus-row" : ""}>
                <span>통로</span>
                <strong>{aisleLabel(comparison.a?.aisle)}</strong>
                <strong>{aisleLabel(comparison.b?.aisle)}</strong>
              </div>
              <div className={criterion === "center" ? "aj-focus-row" : ""}>
                <span>줄 가운데</span>
                <strong>
                  {comparison.a ? `${comparison.a.centerOffset}석 옆` : "모름"}
                </strong>
                <strong>
                  {comparison.b ? `${comparison.b.centerOffset}석 옆` : "모름"}
                </strong>
              </div>
              <div>
                <span>열 순서</span>
                <strong>
                  {comparison.a
                    ? `${comparison.a.floor} ${comparison.a.rowIndex}/${comparison.a.rowCount}`
                    : "모름"}
                </strong>
                <strong>
                  {comparison.b
                    ? `${comparison.b.floor} ${comparison.b.rowIndex}/${comparison.b.rowCount}`
                    : "모름"}
                </strong>
              </div>
              <div className="aj-layer-label">
                내가 넣은 예상 가격 · 실제 판매가 아님
              </div>
              <div className={criterion === "price" ? "aj-focus-row" : ""}>
                <span>가격</span>
                {[
                  { id: "A", seat: a, set: setA },
                  { id: "B", seat: b, set: setB },
                ].map((item) => (
                  <label key={item.id}>
                    <span className="sr-only">{item.id} 예상 가격</span>
                    <input
                      type="number"
                      min="0"
                      max="10000000"
                      value={item.seat?.expectedPrice ?? ""}
                      onChange={(event) => {
                        const price =
                          event.target.value === ""
                            ? null
                            : Number(event.target.value);
                        if (
                          price !== null &&
                          (!Number.isInteger(price) ||
                            price < 0 ||
                            price > 10000000)
                        )
                          return;
                        if (item.seat)
                          item.set({ ...item.seat, expectedPrice: price });
                        setChosen(null);
                      }}
                    />
                    <small>원</small>
                  </label>
                ))}
              </div>
            </section>
            <div className="aj-unknown">
              <strong>시야 · 이번 공연의 무대는 모름</strong>
              <p>
                시설 전사 좌표는 공연별 가림·시야·좌석 재고를 확인해 주지
                않아요.
              </p>
            </div>
            <section className="aj-result" aria-live="polite">
              <span>기준 · {seatDuelLabels[criterion]}</span>
              <h2>
                {comparison.winner
                  ? `${comparison.winner}가 이 기준에 맞아요.`
                  : "이 기준만으로는 순서를 정하지 못해요."}
              </h2>
              <p>{comparison.reason}</p>
              {criterion === "price" && comparison.winner && (
                <p className="aj-tradeoff">
                  구조는 따로 봐요. {comparison.winner}는{" "}
                  {aisleLabel(
                    (comparison.winner === "A" ? comparison.a : comparison.b)
                      ?.aisle,
                  )}
                  이며, 줄 가운데에서{" "}
                  {
                    (comparison.winner === "A" ? comparison.a : comparison.b)
                      ?.centerOffset
                  }
                  석 옆이에요.
                </p>
              )}
            </section>
            {comparison.winner ? (
              <button
                className="aj-primary"
                disabled={!comparison.valid}
                onClick={() => choose(comparison.winner!)}
              >
                {comparison.winner} 우선,{" "}
                {comparison.winner === "A" ? "B" : "A"} 대안으로 보기 →
              </button>
            ) : (
              <div className="aj-tie">
                <p>확인된 차이를 보고 직접 1순위를 골라요.</p>
                {(["A", "B"] as const).map((id) => (
                  <button
                    className="button"
                    key={id}
                    disabled={!comparison.valid}
                    onClick={() => choose(id)}
                  >
                    {id}로 정하기
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        {(step === "decision" || step === "saved") && selected && other && (
          <>
            <section className="aj-decision-sheet">
              <div className="aj-selected">
                <i className={chosen === "A" ? "aj-a" : "aj-b"}>{chosen}</i>
                <div>
                  <span>1순위 · 내 기준 {seatDuelLabels[criterion]}</span>
                  <h2>{label(selected)}</h2>
                </div>
              </div>
              <p>{comparison.reason}</p>
              <div className="aj-alternative">
                <span>놓치면 대안</span>
                <strong>{label(other)}</strong>
              </div>
              <div className="aj-sheet-unknown">
                이번 회차 무대 · 실제 시야 · 좌석 재고는 확인 전
              </div>
            </section>
            {step === "decision" ? (
              <>
                <label className="aj-note">
                  공연·회차 메모 (선택)
                  <input
                    maxLength={120}
                    value={eventTitle}
                    onChange={(event) => setEventTitle(event.target.value)}
                  />
                </label>
                <label className="aj-note">
                  일행에게 덧붙일 내 말
                  <textarea
                    value={note}
                    maxLength={500}
                    onChange={(event) => setNote(event.target.value)}
                    rows={3}
                  />
                </label>
                <button className="aj-primary" onClick={save}>
                  이 순서와 메모 저장
                </button>
              </>
            ) : (
              <>
                <p className="aj-saved-status">
                  ✓ 이 기기에 저장한 순서예요. 다른 기기로 옮길 때는 설정에서
                  백업해 주세요.
                </p>
                <button className="aj-primary" onClick={() => void copy()}>
                  일행에게 보낼 결정문 복사
                </button>
                <button className="button aj-revise" onClick={revise}>
                  기준 바꿔 다시 고르기
                </button>
              </>
            )}
            <details className="aj-share-text">
              <summary>공유될 원문 보기</summary>
              <pre>{text(step === "saved" ? saved : record())}</pre>
              {step === "decision" && (
                <button className="button" onClick={() => void copy()}>
                  원문 복사
                </button>
              )}
            </details>
          </>
        )}
        {storageProblem && (
          <p className="aj-error">
            기기 저장 상태를 확인해 주세요.{" "}
            <Link to="/settings">설정·데이터 관리</Link>에서 원본과 백업을
            확인할 수 있어요.
          </p>
        )}
        {message && (
          <p className="aj-status" role="status">
            {message}
          </p>
        )}
        {state.seatDecisions.length > 0 && (
          <details className="aj-share-text">
            <summary>
              저장한 결정 다시 열기 ({state.seatDecisions.length})
            </summary>
            <div className="stack">
              {state.seatDecisions.map((value) => (
                <button
                  className="button"
                  key={value.id}
                  onClick={() => reopen(value)}
                >
                  {value.eventTitle ||
                    value.candidates[0]?.venueName ||
                    "좌석 결정"}{" "}
                  · {new Date(value.savedAt).toLocaleDateString("ko-KR")} ·{" "}
                  {label(
                    value.candidates.find(
                      (seat) => seat.candidateId === value.chosenCandidateId,
                    ) ?? null,
                  )}
                </button>
              ))}
            </div>
          </details>
        )}
        <footer className="aj-source">
          <ExternalLink href={layout.source.url}>
            공식 시설 안내 대조 ↗
          </ExternalLink>
          <p>
            {layout.source.label} · 자료 확인일 {layout.source.checkedAt}
            <br />
            이번 공연의 현재 배치는 공식 예매처에서 다시 확인해 주세요.
          </p>
        </footer>
        <nav className="aj-secondary" aria-label="다른 좌석 도구">
          <Link to="/start">다른 공연장·한 자리 찾기</Link>
          <Link to="/compare">후기 근거로 후보 비교</Link>
          <Link to="/explore">공연장과 확인 범위 둘러보기</Link>
          <Link to="/passport">내 기록</Link>
          <Link to="/settings">백업·데이터 관리</Link>
        </nav>
      </div>
      {editing && (
        <SeatPairEditor
          layout={layout}
          venue={venue}
          space={space}
          a={a}
          b={b}
          onClose={() => setEditing(false)}
          onApply={(nextA, nextB) => {
            setA(nextA);
            setB(nextB);
            setChosen(null);
            setMessage("");
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}

function DuelMap({
  layout,
  a,
  b,
  compact,
}: {
  layout: SeatLayoutDefinition;
  a: SeatCandidate | null;
  b: SeatCandidate | null;
  compact: boolean;
}) {
  const sections = layout.floors.flatMap((floor) =>
    floor.sections
      .filter((section) => section.rows?.length)
      .map((section) => ({ floor, section })),
  );
  const [sectionId, setSectionId] = useState("");
  const selected =
    sections.find((item) => item.section.id === sectionId) ??
    sections.find((item) => item.section.label === a?.sectionCode) ??
    sections[0];
  if (!selected) return null;
  const rows = selected.section.rows ?? [];
  const rowWidth = (row: (typeof rows)[number]) =>
    row.seats.length + (row.aisleAfter?.length ?? 0) * 1.4;
  const maxSeats = Math.max(...rows.map(rowWidth));
  const height =
    58 + rows.length * 11 + rows.filter((row) => row.gapBefore).length * 6;
  return (
    <figure className={`aj-map${compact ? " aj-map-compact" : ""}`}>
      {sections.length > 1 && (
        <label className="aj-map-floor">
          표시 구역
          <select
            aria-label="좌석도 표시 구역"
            value={selected.section.id}
            onChange={(event) => setSectionId(event.target.value)}
          >
            {sections.map((item) => (
              <option key={item.section.id} value={item.section.id}>
                {item.floor.label} · {item.section.shortLabel}
              </option>
            ))}
          </select>
        </label>
      )}
      <svg
        viewBox={`0 0 540 ${height}`}
        role="img"
        aria-label={`${layout.title} ${selected.section.label} ${rows.length}개 열의 전사 좌표. ${a ? `후보 A ${label(a)}.` : "후보 A 미입력."} ${b ? `후보 B ${label(b)}.` : "후보 B 미입력."} 시야나 물리적 비례를 표현하지 않아요.`}
      >
        <rect
          x="130"
          y="8"
          width="280"
          height="18"
          rx="3"
          className="aj-stage"
        />
        <text x="270" y="21" textAnchor="middle" fill="white" fontSize="9">
          {layout.surfaceLabel}
        </text>
        {rows.map((row, rowIndex) => {
          const y =
            42 +
            rowIndex * 11 +
            rows.slice(0, rowIndex + 1).filter((item) => item.gapBefore)
              .length *
              6;
          return (
            <g key={row.label}>
              <text x="4" y={y + 3} className="aj-row-number" fontSize="7">
                {row.label}
              </text>
              {row.seats.map((number, index) => {
                const x =
                  26 +
                  ((maxSeats - rowWidth(row)) / 2 +
                    index +
                    (row.aisleAfter?.filter(
                      (seat) => row.seats.indexOf(seat) < index,
                    ).length ?? 0) *
                      1.4) *
                    (490 / maxSeats);
                const marker = [
                  a?.sectionCode === selected.section.label &&
                  a.rowLabel === row.label &&
                  a.seatNumber === number
                    ? "A"
                    : "",
                  b?.sectionCode === selected.section.label &&
                  b.rowLabel === row.label &&
                  b.seatNumber === number
                    ? "B"
                    : "",
                ].filter(Boolean)[0];
                return (
                  <g key={number}>
                    <rect
                      x={x}
                      y={y - 3}
                      width="6"
                      height="6"
                      rx="1"
                      className="aj-seat-dot"
                    />
                    {marker && (
                      <g>
                        {marker === "A" ? (
                          <circle
                            cx={x + 3}
                            cy={y}
                            r="9"
                            className="aj-marker-a"
                          />
                        ) : (
                          <rect
                            x={x - 6}
                            y={y - 9}
                            width="18"
                            height="18"
                            rx="2"
                            className="aj-marker-b"
                          />
                        )}
                        <text
                          x={x + 3}
                          y={y + 3}
                          textAnchor="middle"
                          fill="var(--paper)"
                          fontSize="9"
                          fontWeight="700"
                        >
                          {marker}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <figcaption>
        시설 전사 좌표 · {selected.floor.label} {rows.length}개 열<br />
        시야·객석의 물리적 비례를 재현한 그림은 아니에요.
      </figcaption>
    </figure>
  );
}

function SeatPairEditor({
  layout,
  venue,
  space,
  a,
  b,
  onClose,
  onApply,
}: {
  layout: SeatLayoutDefinition;
  venue: (typeof starterVenues)[number];
  space: (typeof starterVenues)[number]["spaces"][number];
  a: SeatCandidate | null;
  b: SeatCandidate | null;
  onClose(): void;
  onApply(a: SeatCandidate, b: SeatCandidate): void;
}) {
  const sections = layout.floors.flatMap((floor) =>
    floor.sections.filter((section) => section.rows?.length),
  );
  const unsupportedSection = (seat: SeatCandidate | null) =>
    seat !== null && !sections.some((section) => section.label === seat.sectionCode);
  const initial = (seat: SeatCandidate | null) => ({
    sectionCode: unsupportedSection(seat) ? "" : seat?.sectionCode ?? sections[0]?.label ?? "",
    rowLabel: unsupportedSection(seat) ? "" : seat?.rowLabel ?? "",
    number: seat ? String(seat.seatNumber) : "",
    price: seat?.expectedPrice == null ? "" : String(seat.expectedPrice),
  });
  const [drafts, setDrafts] = useState([initial(a), initial(b)]);
  const [error, setError] = useState("");
  const candidate = (index: number): SeatCandidate | null => {
    const draft = drafts[index]!;
    const number = Number(draft.number),
      price = draft.price.trim() === "" ? null : Number(draft.price);
    if (
      !draft.sectionCode ||
      !draft.rowLabel ||
      !draft.number.trim() ||
      !Number.isInteger(number) ||
      (price !== null &&
        (!Number.isInteger(price) || price < 0 || price > 10000000))
    )
      return null;
    return {
      candidateId: (index === 0 ? a : b)?.candidateId ?? candidateId(),
      categoryId: venue.categoryId,
      venueId: venue.id,
      venueName: venue.name,
      spaceId: space.id,
      spaceName: space.name,
      layoutVersionId: layout.layoutVersionId,
      instanceId: (index === 0 ? a : b)?.instanceId ?? null,
      instanceLabel: (index === 0 ? a : b)?.instanceLabel ?? null,
      sectionCode: draft.sectionCode,
      rowLabel: draft.rowLabel,
      seatNumber: number,
      expectedPrice: price,
    };
  };
  const update = (index: number, values: Partial<(typeof drafts)[number]>) => {
    setDrafts((current) =>
      current.map((row, i) => (i === index ? { ...row, ...values } : row)),
    );
    setError("");
  };
  return (
    <BottomSheet title="예매 중인 두 자리" onClose={onClose}>
      <div className="seat-duel aj-editor">
        <p>시설 전사에 있는 열·번호를 넣어요. 가격은 선택 입력이에요.</p>
        {drafts.map((draft, index) => (
          <fieldset className="aj-edit-seat" key={index}>
            <legend>후보 {index === 0 ? "A" : "B"}</legend>
            {unsupportedSection(index === 0 ? a : b) && (
              <p>
                기존 표기: {label(index === 0 ? a : b)}. 현재 시설 구역에서 다시 골라 주세요.
                원래 후보·저장 기록은 그대로예요.
              </p>
            )}
            <label>
              구역
              <select
                value={draft.sectionCode}
                onChange={(event) =>
                  update(index, {
                    sectionCode: event.target.value,
                    rowLabel: "",
                  })
                }
              >
                {!draft.sectionCode && <option value="">구역을 다시 선택해 주세요</option>}
                {sections.map((section) => (
                  <option key={section.id} value={section.label}>
                    {section.label}
                  </option>
                ))}
              </select>
            </label>
            <div>
              <label>
                열
                <select
                  aria-label={`${index === 0 ? "A" : "B"} 열`}
                  disabled={!draft.sectionCode}
                  value={draft.rowLabel}
                  onChange={(event) =>
                    update(index, { rowLabel: event.target.value })
                  }
                >
                  <option value="">열 선택</option>
                  {sections
                    .find((section) => section.label === draft.sectionCode)
                    ?.rows?.map((row) => (
                      <option key={row.label} value={row.label}>
                        {row.label}열
                      </option>
                    ))}
                </select>
              </label>
              <label>
                번호
                <input
                  aria-label={`${index === 0 ? "A" : "B"} 번호`}
                  type="number"
                  min="1"
                  value={draft.number}
                  onChange={(event) =>
                    update(index, { number: event.target.value })
                  }
                />
              </label>
            </div>
            <label>
              내 예상 가격 (원)
              <input
                aria-label={`${index === 0 ? "A" : "B"} 예상 가격 입력`}
                type="number"
                min="0"
                max="10000000"
                value={draft.price}
                onChange={(event) =>
                  update(index, { price: event.target.value })
                }
              />
            </label>
          </fieldset>
        ))}
        {error && (
          <p className="aj-error" role="alert">
            {error}
          </p>
        )}
        <button
          className="aj-primary"
          onClick={() => {
            const nextA = candidate(0),
              nextB = candidate(1);
            if (
              !nextA ||
              !nextB ||
              !compareSeatDuel(layout, nextA, nextB, "aisle").valid
            ) {
              setError(
                "전사에 있는 서로 다른 두 좌석을 넣어 주세요. 열마다 실제 번호가 달라요. 예상 가격은 0~1천만원의 정수로 넣어 주세요.",
              );
              return;
            }
            onApply(nextA, nextB);
          }}
        >
          좌석도에 반영하기
        </button>
      </div>
    </BottomSheet>
  );
}
